import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import BoxIconOpen from '../components/BoxIconOpen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const verifyingRef = React.useRef(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token && !verifyingRef.current) {
      verifyingRef.current = true;
      verifyEmail();
    } else if (!token) {
      setStatus('error');
      setError('Lien de vérification invalide');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const res = await fetch(`${API}/auth/verify-email/${token}`);
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('sendbox_token', data.token);
        setStatus('success');
        setTimeout(() => refreshUser(), 500);
      } else {
        setStatus('error');
        setError(data.detail || 'Lien de vérification invalide ou expiré');
      }
    } catch {
      setStatus('error');
      setError('Erreur de connexion au serveur');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <header className="px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <a href="/" className="inline-flex items-center gap-3">
            <BoxIconOpen size={44} />
            <span className="text-2xl font-bold text-white tracking-tight">SENDBOX</span>
          </a>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-3xl p-10 max-w-md w-full text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2">Vérification en cours...</h1>
              <p className="text-blue-300/70">Merci de patienter quelques instants</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2" data-testid="verification-success">Email confirmé !</h1>
              <p className="text-blue-300/70 mb-8">
                Votre compte est maintenant actif. Bienvenue sur SENDBOX !
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-6"
                data-testid="go-home-after-verify-btn"
              >
                Commencer à partager des fichiers
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Vérification échouée</h1>
              <p className="text-blue-300/70 mb-6">{error}</p>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-blue-500/30 text-blue-300"
                data-testid="go-home-error-btn"
              >
                Retour à l'accueil
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default VerifyEmailPage;
